import React from 'react';

import {useDesktopMenu} from './state';

// https://stackoverflow.com/questions/12279113/recommended-wai-aria-implementation-for-navigation-bar-menu

const {RawHTML} = polyphony.components.generic;
const {prefixCSS} = polyphony.css;
const {useHotkeys, useA11y, useRefWithListeners} = polyphony.hooks;
const {combineRefs} = polyphony.util;

export const isMenuDescendant = (item, parent_id) => {
    if (!item || !parent_id) return false;
    if (item.id === parent_id) return true;
    for (const i of item.children) {
        if (isMenuDescendant(i, parent_id)) return true;
    }
    return false;
};

/*const findItem = (items, id, f = (items, index, i) => i) => {
    for (let x = 0; x < items.length; x++) {
        const i = items[x];
        if (i.id === id) return f(items, x, i);
        let v;
        if (i.children && (v = findItem(i.children))) {
            return v;
        }
    }
    return null;
};*/

const styles = {
    link: prefixCSS({
        flex: 'none',
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        height: '100%'
    }),
    childrenWrapper: prefixCSS({
        zIndex: 1000,
        height: 'auto',
        width: 'auto',
        minWidth: 100,
        display: 'flex',
        flexDirection: 'column'
    }),
    title: {
        color: 'inherit',
        textAlign: 'center',
        width: '100%',
        padding: 5,
        boxSizing: 'border-box'
    }
};

const DesktopMenuItem = ({item, depth=0, parent: parentItem, index, isFirst=false, isLast=false}) => { // eslint-disable-line no-unused-vars
    const {hoveredItem, setHoveredItem, items,
           isColor, uniformWidth, headerHeight} = useDesktopMenu();

    const {children, title, id, /*parent_id, */url} = item;

    const col = parentItem ? parentItem.children : items;

    const menuItems = {
        next: isLast ? null : col[index + 1],
        previous: isFirst ? null : col[index - 1],
        forward: item && item.children && item.children.length > 0 ? item.children[0] : null,
        backward: parentItem,
        isFirst,
        isLast
    };

    const nextSibling = e => {
        e.preventDefault();
        polyphony.setFocusMode('tab');
        if (menuItems.next) setHoveredItem(menuItems.next);
    };

    const previousSibling = e => {
        e.preventDefault();
        polyphony.setFocusMode('tab');
        if (menuItems.previous) setHoveredItem(menuItems.previous);
    };

    const openSubmenu = e => {
        e.preventDefault();
        polyphony.setFocusMode('tab');
        if (menuItems.forward) setHoveredItem(menuItems.forward);
    };

    const closeSubmenu = e => {
        e.preventDefault();
        polyphony.setFocusMode('tab');
        if (menuItems.backward) setHoveredItem(menuItems.backward);
    };

    React.useLayoutEffect(() => {
        if (lnrRef.current) {
            if (hoveredItem && hoveredItem.id === id) {
                lnrRef.current.focus();
            }
        }
    }, [hoveredItem]);

    const hkref = useHotkeys({
        Space: e => e.target.click(),
        Enter: e => e.target.click(),
        ArrowRight: depth % 2 === 1 ? openSubmenu : nextSibling,
        ArrowLeft: depth % 2 === 0 && !isFirst ? previousSibling : closeSubmenu,
        ArrowDown: depth % 2 === 1 ? nextSibling : openSubmenu,
        ArrowUp: depth % 2 === 1 && !isFirst ? previousSibling : closeSubmenu
    }, [depth, menuItems, items, setHoveredItem, item, isFirst]);

    const [{/*focus*/}, aref] = useA11y('menuitem', {
        'aria-haspopup': children && children.length > 0 ? 'true' : 'false',
        'aria-expanded': isHover ? 'true' : 'false'
    });

    const lnrRef = useRefWithListeners({
        mouseenter: e => {
            e.target.focus();
            setHoveredItem(item);
        },
        focus: () => setHoveredItem(item),
        blur: () => isLast && depth === 0 ? setHoveredItem(null) : null
    }, [item]);

    const isHover = hoveredItem ? isMenuDescendant(item, hoveredItem.id) : false;

    const height = Math.min(80, headerHeight - 20);

    const isVertical = depth % 2 === 1;
    const isNextVertical = (depth + 1) % 2 === 1;

    const wrapperStyle = {
        height,
        width: depth === 0 ? uniformWidth : undefined,
        [isVertical ? 'paddingTop' : 'paddingLeft']: depth > 0 ? isVertical ? 20 : 10 : undefined,
        display: 'flex',
        flexDirection: isNextVertical ? 'column' : 'row'
    };

    const linkStyle = {
        ...styles.link,
        width: uniformWidth,
        height,
        backgroundColor: depth > 0 ? isHover ? "white" : 'hsla(0, 0%, 100%, 0.67)' : isHover ? "#0F3762" : 'transparent'
    };

    const color = depth > 0 ? isHover ? "#0F3762" : 'gray' : isHover || !isColor ? 'white' : 'black';

    return (
        <li style={prefixCSS(wrapperStyle)} role='none'>
            <a role='menuitem' aria-label={title} style={linkStyle} href={url} ref={combineRefs([aref, hkref, lnrRef])}>
                <span style={{...styles.title, color}}><RawHTML>{title}</RawHTML></span>
            </a>
            {isHover && children && children.length > 0 &&
            <ul role='presentation' style={prefixCSS({display: 'flex', flexDirection: isNextVertical ? 'column' : 'row', flexShrink: 0})}>
                {children.map((c, i) => <DesktopMenuItem key={`${c.id} ${i}`} item={c} parent={item} index={i} depth={depth + 1} isFirst={i === 0} isLast={i === children.length - 1} />)}
            </ul>}
        </li>
    );
};

DesktopMenuItem.displayName = 'DesktopMenuItem';

export default DesktopMenuItem;

